import { useContext, useEffect, useState } from 'react';
import ContentApp from '../../../../components/ContentApp';
import { WelletContext } from '../../../../context/WelletContext';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setReservation } from '../../../../actions/reservationActions';
import { CircleSpinner } from 'react-spinners-kit';
import { fetchProductTransfer } from '../../../../services/transfer/transferService';
import Header from '../../../../components/Common/Header/Header';
import styles from './TransferProductSelector.module.css';
import TransferIcon from '../../../../components/icons/TransferIcon';
import Button from '../../../../components/Common/Button/Button';
import Money from '../../../../components/Common/Money/Money';
import EmptyActivity from '../../../../components/Common/EmptyActivity/EmptyActivity';
import PassengersTransferIcon from '../../../../components/icons/PassengersTransferIcon';

const TransferProductSelector = ({ history }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const welletContext = useContext(WelletContext);
  const language = useSelector((state) => state.app.language);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(null);
  const transfer = useSelector((state) => state.reservation.transfer);
  const reservation = useSelector((state) => state.reservation);
  const [errorMessage, setErrorMessage] = useState(null);

  const getShow = async () => {
    try {
      const result = await welletContext.apis.tickets.get(`/shows/get/`, {
        params: {
          showId: reservation.showId,
          lang: language,
          details: false,
          reservationType: 'RESTAURANT',
          images: false,
          videos: false,
          isFeature: false,
        },
      });

      setShow(result.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getProducts = async () => {
    try {
      const result = await fetchProductTransfer(
        welletContext,
        reservation.showId,
        transfer?.fromZoneId,
        transfer?.toZoneId,
        reservation?.paxs,
        transfer?.transferType,
        language,
      );

      setProducts(result.data);
      if (!result.data || result.data.length === 0) {
        setErrorMessage(t('noTransfersTitle'));
      } else {
        setErrorMessage(null);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const [selectedProductId, setSelectedProductId] = useState(null);
  const handleIncrement = (id) => {
    const index = products.findIndex((p) => p.id === id);
    const product = products[index];

    if (product.stock && product.stock === product.quantity) return;

    const newProducts = [...products];

    newProducts[index].disabled = false;
    newProducts[index].quantity = 1;
  };

  const handleOnReserve = (productId) => {
    if (selectedProductId !== productId) {
      setSelectedProductId(productId);
    }
    handleIncrement(productId);
    const reservationInfo = setReservationInfo();
    dispatch(setReservation(reservationInfo));
    history.push({ pathname: '/complete-transfer-details' });
  };

  const setReservationInfo = () => {
    const finalProducts = products
      .filter((product) => product.quantity > 0)
      .map((product) => ({
        id: product.id,
        quantity: product.quantity,
        extraPaxs: 0,
        specialType: product.specialType,
        paxs: reservation?.paxs,
      }));

    const totalAmount = products
      .filter((p) => p.quantity > 0)
      .reduce((sum, p) => sum + p.prices.price * p.quantity, 0);

    return {
      ...reservation,
      showId: reservation.showId,
      showName: show.name,
      products: finalProducts,
      hasTransportation: false,
      totalAmount,
      calendar: show.calendar,
    };
  };

  useEffect(() => {
    if (reservation) {
      getShow();
      getProducts();
    }
  }, []);
  const handleSelectProduct = (productId) => {
    setSelectedProductId(productId);
    handleIncrement(productId);
  };

  return (
    <>
      {loading || !show ? (
        <div className='loaderDiv'>
          <CircleSpinner
            className='spinner-app mx-auto'
            style={{ margin: 'auto' }}
            size={40}
            color='var(--color-font-primary)'
          />
        </div>
      ) : (
        <>
          <Header />

          <ContentApp>
            <div className={`${styles.header} mb-4`}>
              <div className={`${styles.iconContainer} mb-3`}>
                <TransferIcon size='30px' />
              </div>
              <div className='bold font-large mb-2'>{t('transfer.transfersAvailable')}</div>
              <div
                className='text-secondary'
                dangerouslySetInnerHTML={{
                  __html: t('transfer.findTransfers_' + transfer?.transferType, {
                    from: transfer?.trips[0]?.fromPickupName,
                    to: transfer?.trips[0]?.toPickupName,
                  }),
                }}
              />
            </div>
            {errorMessage && (
              <EmptyActivity subtitle={<div className='bold'>{errorMessage}</div>} />
            )}

            <div className={styles.productsGrid}>
              {products.map((p, index) => (
                <div
                  key={p.id}
                  className={`${styles.card} ${selectedProductId === p.id ? styles.selected : ''} ${
                    !p.hasCapacity ? styles.disabled : ''
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onClick={() => p.hasCapacity && handleSelectProduct(p.id)}
                >
                  {p.images && p.images.length > 0 ? (
                    <div className={styles.vehicleImageContainer}>
                      <img
                        src={p.images[0] || '/placeholder.svg'}
                        alt={p.name}
                        className={styles.vehicleImage}
                      />
                    </div>
                  ) : (
                    <div className={styles.vehicleIconLarge}>
                      <TransferIcon />
                    </div>
                  )}

                  <div className={styles.capacityBadge}>
                    <PassengersTransferIcon />
                    <span>{t('upToPassenger', { pax: p.pax })}</span>
                  </div>

                  <div className='fw-semibold font-normal2 pl-3 mt-3 '>{p.name}</div>

                  <div className='pl-3 mt-2 pr-3  '>
                    {p.description && (
                      <div className='text-secondary font-tiny mb-3'>{p.description}</div>
                    )}

                    {p.productContent && p.productContent.length > 0 && (
                      <div className={styles.productContentContainer}>
                        {p.productContent.map((section, sIndex) => (
                          <div key={sIndex} className='mb-3'>
                            <div className={`${styles.sectionTitle} font-smaller fw-600 `}>
                              {section.title}
                            </div>
                            {section.description && (
                              <div className={styles.sectionDescription}>{section.description}</div>
                            )}
                            {section.items && section.items.length > 0 && (
                              <div className={styles.itemsGrid}>
                                {section.items
                                  .sort((a, b) => a.displayOrder - b.displayOrder)
                                  .map((item, iIndex) => (
                                    <div key={iIndex} className={styles.contentItem}>
                                      {item.iconUrl && (
                                        <img
                                          src={item.iconUrl || '/placeholder.svg'}
                                          alt={item.description}
                                          className={styles.itemIcon}
                                        />
                                      )}
                                      <span className='text-tertiary  font-extra-small fw-500'>
                                        {item.description}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className={`${styles.priceContainer} pt-2 pb-2  `}>
                      <div className='font-tiny  text-secondary fw-500 mt-2 mb-2'>
                        {t('priceFrom')}
                      </div>
                      <div className='bold font-large'>
                        <Money value={p.prices.price} decimal={false} />
                      </div>
                    </div>
                  </div>
                  {p.hasCapacity && (
                    <div className='pl-3 pr-3 pb-3'>
                      <Button
                        text={t('select')}
                        onClick={() => handleOnReserve(p.id)}
                        size='normal'
                        className={'w-100'}
                        icon
                        typeIcon='arrow'
                        iconColor='#000'
                        disabled={!p.hasCapacity}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ContentApp>
        </>
      )}
    </>
  );
};

export default TransferProductSelector;
